import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, Scan, Crown, DollarSign, Coins as CoinsIcon } from "lucide-react";

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [grantAmt, setGrantAmt] = useState({});

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data); setUsers(u.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const grant = async (uid) => {
    const coins = parseInt(grantAmt[uid] || "0", 10);
    if (!coins) return;
    try { await api.post("/admin/coins/grant", { user_id: uid, coins }); toast.success(`Granted ${coins} coins`); load(); }
    catch { toast.error("Grant failed"); }
  };
  const toggleElite = async (u) => {
    try { await api.post(`/admin/users/${u.id}/elite?elite=${!u.elite}`); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!user) return <div className="p-6 text-slate-400">Sign in required</div>;
  if (user.role !== "admin") return <div className="p-6 text-slate-400">Admin only</div>;

  return (
    <div className="min-h-screen pb-24 pt-6 px-4 max-w-3xl mx-auto">
      <h1 className="font-heading text-3xl font-bold">Admin Dashboard</h1>
      <p className="text-slate-400 text-sm">Jixxeo Scan control center</p>

      {stats && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
          <Stat icon={Users} label="Users" value={stats.total_users} color="#00E5FF" testid="stat-users" />
          <Stat icon={Scan} label="Scans" value={stats.total_scans} color="#10B981" testid="stat-scans" />
          <Stat icon={Crown} label="Elite" value={stats.elite_users} color="#F59E0B" testid="stat-elite" />
          <Stat icon={DollarSign} label="Revenue" value={`$${stats.revenue_usd}`} color="#84CC16" testid="stat-revenue" />
          <Stat icon={CoinsIcon} label="Paid tx" value={stats.paid_transactions} color="#EC4899" testid="stat-tx" />
        </div>
      )}

      {stats && (
        <div className="mt-4 jx-glass rounded-2xl p-4">
          <h3 className="font-heading font-bold text-sm">Scans by mode</h3>
          <div className="mt-2 space-y-1.5">
            {stats.scans_by_mode.map((m) => (
              <div key={m.mode} className="flex items-center gap-2 text-xs">
                <span className="w-32 font-mono-data uppercase text-slate-400">{m.mode}</span>
                <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-400" style={{ width: `${Math.min(100, (m.count / (stats.total_scans || 1)) * 100)}%` }} />
                </div>
                <span className="w-10 text-right font-mono-data">{m.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 jx-glass rounded-2xl p-4">
        <h3 className="font-heading font-bold text-sm">Users</h3>
        <div className="mt-3 divide-y divide-white/5">
          {users.map((u) => (
            <div key={u.id} className="py-2 flex items-center gap-2 text-sm" data-testid={`admin-user-${u.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold truncate">{u.username} <span className="text-[11px] text-slate-500 font-mono-data">{u.email}</span></p>
                <p className="text-[11px] text-slate-500 font-mono-data">Lv{u.level} · {u.xp}XP · {u.coins} coins {u.elite && "· Elite"} {u.role === "admin" && "· ADMIN"}</p>
              </div>
              <input placeholder="+coins" value={grantAmt[u.id] || ""} onChange={(e) => setGrantAmt({ ...grantAmt, [u.id]: e.target.value })}
                className="w-20 h-8 px-2 rounded-lg bg-white/5 border border-white/10 text-xs" />
              <button data-testid={`admin-grant-${u.id}`} onClick={() => grant(u.id)} className="px-2 h-8 rounded-lg bg-cyan-400/20 text-cyan-200 border border-cyan-400/30 text-xs">Grant</button>
              <button data-testid={`admin-elite-${u.id}`} onClick={() => toggleElite(u)} className={`px-2 h-8 rounded-lg text-xs border ${u.elite ? "bg-amber-400/20 border-amber-400/40 text-amber-300" : "bg-white/5 border-white/10 text-slate-400"}`}>{u.elite ? "Elite" : "Free"}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="jx-glass rounded-2xl p-3" data-testid={testid}>
      <Icon size={16} style={{ color }} />
      <p className="mt-1 text-[10px] font-mono-data uppercase text-slate-500">{label}</p>
      <p className="font-heading text-xl font-black">{value}</p>
    </div>
  );
}
